import Typography from '@mui/material/Typography';
import { Section } from 'src/@types/Course';
import Iconify from 'src/components/Iconify';
import { Alert, Stack, Button } from '@mui/material';
import SectionsItemTable from '../section-item-table/SectionsItemTable';
import TextMaxLine from 'src/components/TextMaxLine';
import { useCourseSummery } from '../useCourseSummery';
import { Accordion, AccordionDetails, AccordionSummary } from '../styles';
import { SectionStatus } from '../useCourseView';

export const SectionSummery = ({
  section,
  sectionIndex,
  expanded,
  setExpanded,
  status,
  saving,
  readOnly,
  locked,
  onEditSection,
  onDeleteSection,
  onNewLesson,
  onEditLesson,
  onDeleteLesson,
  onMoveLesson,
  onClearError,
}: {
  section: Section;
  sectionIndex: number;
  expanded: false | string;
  setExpanded: React.Dispatch<React.SetStateAction<string | false>>;
  status?: SectionStatus;
  // A request is running somewhere on the page.
  saving: boolean;
  // Partner course: lessons are listed, nothing can be changed here.
  readOnly: boolean;
  // Lesson problems wait for "Fix lesson problems".
  locked: boolean;
  onEditSection: VoidFunction;
  onDeleteSection: VoidFunction;
  onNewLesson: VoidFunction;
  onEditLesson(uuid: string): void;
  onDeleteLesson(uuid: string): void;
  onMoveLesson(uuid: string, delta: number): void;
  onClearError: VoidFunction;
}) => {
  const { handleChange, getMenuItem } = useCourseSummery(
    onEditSection,
    onDeleteSection,
    setExpanded,
    saving || locked
  );
  return (
    <Accordion
      expanded={expanded === `panel${sectionIndex}`}
      onChange={handleChange(`panel${sectionIndex}`)}
      data-section-index={sectionIndex}
    >
      <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
        <Stack sx={{ width: '100%' }} spacing={1}>
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            sx={{ width: '100%' }}
            spacing={3}
            alignItems={'center'}
          >
            <Typography>{section.title}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {status?.saving && (
                <Typography variant="caption" color="text.secondary" role="status">
                  Saving…
                </Typography>
              )}
              {status?.saved && (
                <Typography variant="caption" color="success.main" role="status">
                  Saved
                </Typography>
              )}
              {!readOnly && getMenuItem()}
            </Stack>
          </Stack>
          {/* In the header so it shows whether the section is open or not. */}
          {status?.error && (
            <Alert
              severity="error"
              role="alert"
              onClick={(e) => e.stopPropagation()}
              onClose={(e) => {
                e.stopPropagation();
                onClearError();
              }}
              data-testid="section-error"
            >
              {status.error}
            </Alert>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <>
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <TextMaxLine
              marginBottom={'20px'}
              marginTop={'20px'}
              fontSize={'22px'}
              sx={{ overflow: 'unset' }}
            >
              {'Section Items'}
            </TextMaxLine>
            {!readOnly && (
              <Button
                variant="contained"
                size="small"
                sx={{ height: '40px' }}
                disabled={saving || locked}
                onClick={onNewLesson}
                startIcon={<Iconify icon={'eva:plus-fill'} />}
              >
                New Section Item
              </Button>
            )}
          </Stack>
          <SectionsItemTable
            items={section?.items ?? []}
            saving={saving}
            readOnly={readOnly}
            locked={locked}
            onMove={onMoveLesson}
            onEdit={onEditLesson}
            onDeleteItem={onDeleteLesson}
          />
        </>
      </AccordionDetails>
    </Accordion>
  );
};
